import * as React from 'react';

import classes from './Button.scss';

type Variant = 'primary' | 'secondary' | 'tertiary';
type Size = 'md' | 'sm' | 'lg';

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  fullWidth?: boolean;
  icon?: React.ReactElement;
  label: React.ReactNode;
  subLabel?: string;
  size?: Size;
};

const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    { type = 'button', variant = 'primary', size = 'md', fullWidth = false, icon, label, subLabel, className, ...rest },
    ref
  ) => {
    const variantClassNameMap: Record<Variant, string> = {
      primary: classes['buttonVariantPrimary'],
      secondary: classes['buttonVariantSecondary'],
      tertiary: classes['buttonVariantTertiary'],
    };
    const variantClassName = variantClassNameMap[variant];
    const classNames = [classes['button'], variantClassName];
    if (size === 'lg') {
      classNames.push(classes['button--large']);
    }
    if (size === 'sm') {
      classNames.push(classes['button--small']);
    }
    if (icon) {
      classNames.push(classes['button--with-icon']);
    }
    if (fullWidth) {
      classNames.push(classes['button--full-width']);
    }
    if (className) {
      classNames.push(className);
    }

    const renderIcon = () => {
      if (!icon) {
        return null;
      }
      return <span className={classes['button__icon']}>{icon}</span>;
    };

    const renderLabel = () => {
      if (!subLabel) {
        return label;
      }
      return (
        <span className={classes['button__text']}>
          <span className={classes['button__text--small']}>{subLabel}</span>
          {label}
        </span>
      );
    };

    return (
      <button
        ref={ref}
        type={type}
        className={classNames.join(' ')}
        {...rest}
      >
        {renderIcon()}
        {renderLabel()}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
